import Head from 'next/head';
import { useRouter } from 'next/router';
import ErrorPage from 'next/error';
import { useState, useRef, useEffect } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@helpers/async';
import { initializeApollo, addApolloState } from '@apollo-client';
import { Box, Link, Grid, GridItem, Text, Heading } from '@terminal/design-system';
import NextNprogress from 'nextjs-progressbar';

import SidebarLayout from '@components/resources/SidebarLayout';
import Card from '@components/resources/Card/Card';
import CATEGORIES from '@components/resources/CategoriesEngineers';

import sharecardImage from '@public/images/resources/sharecard.jpg';

import dynamic from 'next/dynamic';

import { primaryCategory, filterPostsByCategory } from '@helpers/categoryFilters';
import infiniteScroll from '@components/resources/infiniteScroll';
import metaRobots from '@helpers/metaRobots';
import sitePath from '@helpers/sitePath';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const ModalBottom = dynamic(() => import('../../../../components/resources/ModalBottom'), {
  ssr: false,
});

const GET_POSTS = gql`
  query GetGlobalEngineerNodes {
    globalEngineers(first: 1000) {
      nodes {
        id
        slug
        title
        categories {
          edges {
            isPrimary
            node {
              name
            }
          }
        }
        featuredImage {
          node {
            altText
            sourceUrl
          }
        }
        globalEngineerACF {
          date
          time
          text
          speakers {
            name
            jobTitle
            image {
              sourceUrl
            }
          }
        }
      }
    }
  }
`;

export async function getServerSideProps({ locale }) {
  const apolloClient = initializeApollo();

  await apolloClient.query({
    query: GET_POSTS,
  });

  return addApolloState(apolloClient, {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  });
}

export default function GlobalEngineer() {
  const router = useRouter();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  let renderPosts = [];

  // infinite scroll
  const [itemsToShow, setItemsToShow] = useState(3);

  const itemsEnd = useRef(null);

  useEffect(() => {
    infiniteScroll(renderPosts, itemsEnd, itemsToShow, setItemsToShow);
  }, [renderPosts, itemsToShow]);

  // Client side Apollo
  const { loading, error, data } = useQuery(GET_POSTS);

  // CSR loading
  if (loading)
    return (
      <NextNprogress
        color="#2D445D"
        startPosition={0.3}
        stopDelayMs={200}
        height={3}
        showOnShallow={true}
      />
    );
  // Apollo error
  if (error) return <ErrorPage statusCode="500" />;

  // category filter

  const categoryQuery = router.query.category;

  const filterSinglePostByCategory = (category, post) => {
    return post.categories.edges.map((cat) => cat.node.name).includes(category);
  };

  const posts = data.globalEngineers.nodes;

  const matchedCategory = CATEGORIES.filter((cat) => cat.slug === categoryQuery)[0]?.label;

  const matchedCategoryDescription = CATEGORIES.filter((cat) => cat.slug === categoryQuery)[0]
    ?.description;

  if (
    categoryQuery &&
    CATEGORIES.map((cat) => cat.slug).includes(categoryQuery) &&
    categoryQuery !== 'latest'
  ) {
    renderPosts = filterPostsByCategory(posts, matchedCategory);
  } else {
    renderPosts = posts;
  }

  if (renderPosts.length) {
    return (
      <>
        <Head>
          <link rel="canonical" href={sitePath(router.asPath)} />
          <title>The Global Engineer - Terminal.io</title>
          <meta name="description" content="" />
          <meta property="og:title" content="The Global Engineer - Terminal.io" />
          <meta property="og:description" content="" />
          <meta property="og:url" content={sitePath(router.asPath)} />
          <meta property="og:image" content={sharecardImage.src} />
          <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
        </Head>
        <SidebarLayout type="engineers">
          {categoryQuery && categoryQuery !== 'latest' && (
            <Box>
              <Heading
                as="h1"
                fontSize={{
                  base: '1.3rem',
                  md: '1.625rem',
                }}
                fontWeight="600"
                lineHeight={{
                  base: '1.5rem',
                  md: '1.875rem',
                }}
                mt={{ lg: '0.8125rem' }}
                color="#2D445D"
              >
                {matchedCategory}
              </Heading>
              <Text
                mt="0.5rem"
                fontSize="0.875rem"
                lineHeight="1.25rem"
                fontStyle="italic"
                color="#6C6C6C"
              >
                {matchedCategoryDescription}
              </Text>
              <Box
                w="100%"
                height={{ base: '0.0625rem' }}
                m={{ base: '1.5rem 0', xl: '2.5rem 0' }}
                bgColor="#D8D8D8"
              />
            </Box>
          )}

          <Grid
            templateColumns={{
              base: '1fr',
              md: '1fr 1fr',
              xl: '1fr 1fr 1fr',
            }}
            gap={{ base: '2.5rem', lg: '2.5rem 1.865rem' }}
          >
            {renderPosts.slice(0, itemsToShow).map((post) => {
              const postImage = post.featuredImage
                ? post.featuredImage.node.sourceUrl
                : sharecardImage.src;

              const postAltText = post.featuredImage ? post.featuredImage.node.altText : '';
              return (
                <GridItem key={post.id}>
                  <Link
                    href={`/engineers/the-global-engineer/archive/${post.slug}`}
                    display="block"
                    maxW={{ base: '18.4375rem', sm: '26.875rem' }}
                    ml="auto"
                    mr="auto"
                    role="group"
                    _hover={{ textDecoration: 'none' }}
                    position="relative"
                  >
                    {filterSinglePostByCategory('Upcoming', post) && (
                      <Box
                        position="absolute"
                        top="1.3125rem"
                        right="0"
                        p="0.375rem 0.625rem"
                        fontSize="0.5625rem"
                        lineHeight="0.625rem"
                        letterSpacing="0.0625rem"
                        bgColor="#7AA6B9"
                        color="#fff"
                        textTransform="uppercase"
                        fontWeight="700"
                        zIndex="1"
                      >
                        Register Now
                      </Box>
                    )}
                    <Card
                      categoryTitle={`${primaryCategory(post)} | The Global Engineer`}
                      imageUrl={postImage}
                      altText={postAltText}
                      cardTitle={post.title}
                    />
                  </Link>
                </GridItem>
              );
            })}
          </Grid>
          <Box ref={itemsEnd} h="4rem" />

          <ModalBottom />
        </SidebarLayout>
      </>
    );
  } else {
    return (
      <>
        <Head>
          <link rel="canonical" href={sitePath(router.asPath)} />
          <title>The Global Engineer - Terminal.io</title>
        </Head>
        <SidebarLayout type="engineers">
          <Box>
            No recordings in the selected category. Please choose a different category or check back
            later.
          </Box>
        </SidebarLayout>
      </>
    );
  }
}
